// Blip, drawn in code: the cube from Blip's lessons, with his face, his
// antenna and its gold plus, and his shadow, shaded as the lessons shade him.
// The online IDE's Blip helper draws him with it (web/ide/src/blip/
// character.js); it is made from the lessons' drawCube, drawFace and
// drawAntenna, which they keep copies of for now.
//
//   BlipCharacter.draw(ctx, body, time, { palette, reduceMotion })
//
// body: { x, y (where he stands), size, yaw, pitch, roll (degrees), lift
// (height above his shadow), sq, st (squash and stretch), eyes ('normal',
// 'happy', 'wide'), mouth ('smile', 'talk', 'o', 'grin', 'yawn'), talk (how
// open, 0 to 1), blink (0 to 1), lookX, lookY (-1 to 1), glow (0 to 1, his
// plus lit up), alpha }. time, in seconds, wobbles his antenna.
// BlipCharacter.palette(element) reads the colours from the editor's CSS
// properties (--editor-* in the IDE, --b3d-editor-* in the help), or else
// uses the desktop editor's own.
(function () {
  'use strict';
  const DEFAULTS = {
    num: '#33ffdd', comment: '#ffee00', white: '#ffffff', deep: '#12344f',
  };
  function rgbOf(c) {
    if (c[0] === '#') {
      const n = parseInt(c.slice(1), 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    const m = c.match(/[\d.]+/g) || [0, 0, 0];
    return [+m[0], +m[1], +m[2]];
  }
  function hex(r, g, b) {
    const v = (x) => Math.max(0, Math.min(255, Math.round(x)));
    return '#' + ((1 << 24) | (v(r) << 16) | (v(g) << 8) | v(b)).toString(16).slice(1);
  }
  function mix(a, b, t) {
    const A = rgbOf(a), B = rgbOf(b);
    return hex(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t);
  }
  function fade(c, a) {
    const A = rgbOf(c);
    return 'rgba(' + A[0] + ',' + A[1] + ',' + A[2] + ',' + a + ')';
  }
  const clamp = (x, lo = 0, hi = 1) => (x < lo ? lo : x > hi ? hi : x);

  const DEG = Math.PI / 180;
  const CUBE_V = [[-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1], [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]];
  const CUBE_F = [
    { v: [4, 5, 6, 7], n: [0, 0, 1] },
    { v: [1, 0, 3, 2], n: [0, 0, -1] },
    { v: [5, 1, 2, 6], n: [1, 0, 0] },
    { v: [0, 4, 7, 3], n: [-1, 0, 0] },
    { v: [7, 6, 2, 3], n: [0, 1, 0] },
    { v: [0, 1, 5, 4], n: [0, -1, 0] },
  ];
  function matMul(A, B) {
    const R = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) R[i][j] = A[i][0] * B[0][j] + A[i][1] * B[1][j] + A[i][2] * B[2][j];
    }
    return R;
  }
  function apply(M, v) {
    return [
      M[0][0] * v[0] + M[0][1] * v[1] + M[0][2] * v[2],
      M[1][0] * v[0] + M[1][1] * v[1] + M[1][2] * v[2],
      M[2][0] * v[0] + M[2][1] * v[1] + M[2][2] * v[2],
    ];
  }
  const rotX = (a) => { const c = Math.cos(a), s = Math.sin(a); return [[1, 0, 0], [0, c, -s], [0, s, c]]; };
  const rotY = (a) => { const c = Math.cos(a), s = Math.sin(a); return [[c, 0, s], [0, 1, 0], [-s, 0, c]]; };
  const rotZ = (a) => { const c = Math.cos(a), s = Math.sin(a); return [[c, -s, 0], [s, c, 0], [0, 0, 1]]; };
  const orient = (yaw, pitch, roll) => matMul(rotY(yaw * DEG), matMul(rotX(pitch * DEG), rotZ(roll * DEG)));
  function norm3(v) { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; }
  const dot3 = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const TOON_LIGHT = norm3([-0.45, 0.8, 0.6]);

  function palette(element) {
    const style = element ? getComputedStyle(element) : null;
    const probe = document.createElement('canvas').getContext('2d');
    const read = (names, fallback) => {
      for (const name of names) {
        const value = style && style.getPropertyValue(name).trim();
        if (value) {
          probe.fillStyle = fallback;
          probe.fillStyle = value;
          return probe.fillStyle;
        }
      }
      return fallback;
    };
    return {
      num: read(['--editor-number', '--b3d-editor-number'], DEFAULTS.num),
      comment: read(['--editor-comment', '--b3d-editor-comment'], DEFAULTS.comment),
      white: read(['--editor-identifier', '--b3d-editor-identifier'], DEFAULTS.white),
      deep: read(['--deep', '--b3d-help-deep'], DEFAULTS.deep),
    };
  }

  // A cube centred on cx,cy, shaded like a cartoon, with an outline.
  function drawCube(ctx, C, cx, cy, size, M, o) {
    const s = size / 2, D = 6;
    const sx = o.sx || 1, sy = o.sy || 1, sz = o.sz || 1;
    const proj = (v) => { const k = D / (D - v[2]); return [cx + v[0] * s * k, cy - v[1] * s * k]; };
    const P = CUBE_V.map((v) => proj(apply(M, [v[0] * sx, v[1] * sy, v[2] * sz])));
    const faces = [];
    CUBE_F.forEach((f, fi) => {
      const n = apply(M, f.n);
      const c = apply(M, [f.n[0] * sx, f.n[1] * sy, f.n[2] * sz]);
      if (-n[0] * c[0] - n[1] * c[1] + n[2] * (D - c[2]) <= 0) return;
      faces.push({ fi, n, pts: f.v.map((i) => P[i]) });
    });
    const path = (pts) => {
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < 4; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.closePath();
    };
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.strokeStyle = C.deep;
    ctx.lineWidth = Math.max(2, size * 0.09);
    faces.forEach((f) => { path(f.pts); ctx.stroke(); });
    faces.forEach((f) => {
      const dif = Math.max(0, dot3(f.n, TOON_LIGHT));
      let col = mix(C.deep, C.num, 0.4 + 0.6 * dif);
      if (dif > 0.8) col = mix(col, C.white, (dif - 0.8) * 1.2);
      path(f.pts);
      ctx.fillStyle = col;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = col;
      ctx.stroke();
    });
    ctx.strokeStyle = fade(C.deep, 0.35);
    ctx.lineWidth = Math.max(1, size * 0.018);
    faces.forEach((f) => { path(f.pts); ctx.stroke(); });
    ctx.restore();
    return { faces, proj, M, s, sx, sy, sz };
  }

  function drawFace(ctx, C, r, b) {
    const front = r.faces.find((f) => f.fi === 0);
    if (!front) return;
    const k = clamp(front.n[2] * 2.2);
    if (k <= 0.02) return;
    const { proj, M, sx, sy, sz } = r;
    const O = proj(apply(M, [0, 0, sz])), U = proj(apply(M, [sx, 0, sz])), V = proj(apply(M, [0, sy, sz]));
    ctx.save();
    ctx.globalAlpha *= k;
    // From here on, one unit is half the face and +y points up the face.
    ctx.transform(U[0] - O[0], U[1] - O[1], V[0] - O[0], V[1] - O[1], O[0], O[1]);
    ctx.fillStyle = C.deep;
    ctx.strokeStyle = C.deep;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const ex = 0.36, ey = 0.14 + (b.lookY || 0) * 0.06, eo = (b.lookX || 0) * 0.1;
    if (b.eyes === 'happy') {
      ctx.lineWidth = 0.09;
      [-ex, ex].forEach((x) => {
        ctx.beginPath();
        ctx.moveTo(x - 0.12 + eo, ey - 0.03);
        ctx.quadraticCurveTo(x + eo, ey + 0.2, x + 0.12 + eo, ey - 0.03);
        ctx.stroke();
      });
    } else {
      const wide = b.eyes === 'wide';
      const ew = wide ? 0.13 : 0.105, eh = (wide ? 0.21 : 0.17) * Math.max(0.1, 1 - (b.blink || 0));
      [-ex, ex].forEach((x) => {
        ctx.beginPath();
        ctx.ellipse(x + eo, ey, ew, eh, 0, 0, Math.PI * 2);
        ctx.fill();
      });
      if (eh > 0.08) {
        ctx.fillStyle = C.white;
        [-ex, ex].forEach((x) => {
          ctx.beginPath();
          ctx.arc(x + eo + 0.035, ey + eh * 0.42, 0.042, 0, Math.PI * 2);
          ctx.fill();
        });
        ctx.fillStyle = C.deep;
      }
    }
    const my = -0.36;
    ctx.lineWidth = 0.085;
    ctx.beginPath();
    switch (b.mouth) {
      case 'talk':
        ctx.moveTo(-0.2, my + 0.06);
        ctx.quadraticCurveTo(0, my + 0.02, 0.2, my + 0.06);
        ctx.quadraticCurveTo(0, my - 0.06 - 0.3 * (b.talk || 0), -0.2, my + 0.06);
        ctx.fill();
        break;
      case 'o':
        ctx.ellipse(0, my, 0.1, 0.13, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'grin':
        ctx.moveTo(-0.26, my + 0.1);
        ctx.lineTo(0.26, my + 0.1);
        ctx.quadraticCurveTo(0, my - 0.42, -0.26, my + 0.1);
        ctx.fill();
        break;
      case 'yawn':
        ctx.ellipse(0, my - 0.03, 0.15, 0.2, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      default:
        ctx.moveTo(-0.22, my + 0.07);
        ctx.quadraticCurveTo(0, my - 0.2, 0.22, my + 0.07);
        ctx.stroke();
    }
    ctx.restore();
  }

  function drawAntenna(ctx, C, r, time, still, glow) {
    const { proj, M, sy, s } = r;
    const wob = still ? 0 : Math.sin(time * 3.1) * 0.08;
    const base = proj(apply(M, [0, sy, 0])), tip = proj(apply(M, [wob, sy + 0.62, 0]));
    ctx.save();
    ctx.strokeStyle = C.deep;
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(1.5, s * 0.09);
    ctx.beginPath();
    ctx.moveTo(base[0], base[1]);
    ctx.lineTo(tip[0], tip[1]);
    ctx.stroke();
    // A little gold plus, like the one in the Blitz3D+ logo, which glows
    // while he thinks.
    const ps = Math.max(2.5, s * 0.22);
    ctx.translate(tip[0], tip[1]);
    if (glow > 0) {
      const halo = ctx.createRadialGradient(0, 0, 0, 0, 0, ps * 3);
      halo.addColorStop(0, fade(C.comment, 0.55 * glow));
      halo.addColorStop(1, fade(C.comment, 0));
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(0, 0, ps * 3, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.rotate(still ? 0 : Math.sin(time * 1.3) * 0.25);
    const plus = () => {
      ctx.beginPath();
      ctx.moveTo(-ps, 0);
      ctx.lineTo(ps, 0);
      ctx.moveTo(0, -ps);
      ctx.lineTo(0, ps);
      ctx.stroke();
    };
    ctx.lineWidth = ps * 0.95 + Math.max(2, s * 0.09);
    plus();
    ctx.strokeStyle = glow > 0 ? mix(C.comment, C.white, 0.5 * glow) : C.comment;
    ctx.lineWidth = ps * 0.95;
    plus();
    ctx.restore();
  }

  // Draws Blip standing at body.x, body.y.
  function draw(ctx, body, time, options) {
    const b = body || {};
    if (!(b.size > 0)) return null;
    const o = options || {};
    const C = o.palette || DEFAULTS;
    const still = !!o.reduceMotion;
    const sq = b.sq || 0, st = b.st || 0, lift = b.lift || 0;
    const sy = 1 - sq + st, sxz = 1 + sq * 0.55 - st * 0.4;
    const cx = b.x, cy = b.y - lift - b.size * 0.5 * sy - b.size * 0.07;
    const M = orient(b.yaw || 0, b.pitch || 0, b.roll || 0);
    const shadow = clamp(1 - lift / Math.max(1, b.size * 3), 0.25, 1);
    ctx.save();
    ctx.globalAlpha *= b.alpha == null ? 1 : b.alpha;
    ctx.beginPath();
    ctx.ellipse(b.x, b.y + 1, b.size * 0.62 * shadow * sxz, b.size * 0.13 * shadow, 0, 0, Math.PI * 2);
    ctx.fillStyle = fade(C.deep, 0.35 * shadow);
    ctx.fill();
    const r = drawCube(ctx, C, cx, cy, b.size, M, { sx: sxz, sy, sz: sxz });
    drawFace(ctx, C, r, b);
    drawAntenna(ctx, C, r, time || 0, still, b.glow || 0);
    ctx.restore();
    return r;
  }

  window.BlipCharacter = { draw, palette, DEFAULTS };
})();
